import { useLanguage } from "../contexts/LanguageContext";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../Components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../Components/ui/card";
import { Input } from "../Components/ui/input";
import SEO from "../Components/SEO";
import { 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  ArrowRight,
  CheckCircle,
  Shield,
  RefreshCw
} from "lucide-react";

export default function ForgotPassword() {
  const { tx } = useLanguage();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Enter email, 2: Enter verification code, 3: Set new password
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // "success" or "error"
  
  const [formData, setFormData] = useState({
    email: "",
    newPassword: "",
    confirmPassword: ""
  });

  // 获取用户数据（模拟从localStorage获取）
  const getUsers = () => {
    const saved = localStorage.getItem('intelevia_users');
    return saved ? JSON.parse(saved) : [];
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // 生成6位验证码
  const generateVerificationCode = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  // 发送验证码
  const sendVerificationCode = async () => {
    if (!formData.email) {
      setMessage("Please enter your email address");
      setMessageType("error");
      return;
    }

    // 检查邮箱是否存在
    const users = getUsers();
    const userExists = users.find(user => user.email === formData.email);
    
    if (!userExists) {
      setMessage("Email address not found in our system");
      setMessageType("error");
      return;
    }

    setIsLoading(true);
    setMessage("");

    // 模拟发送验证码
    setTimeout(() => {
      const code = generateVerificationCode();
      // 在实际项目中，这里应该通过邮件服务发送验证码
      console.log("Password reset verification code sent to", formData.email, "Code:", code);
      
      // 存储验证码到sessionStorage（仅用于演示）
      sessionStorage.setItem('password_reset_code', code);
      sessionStorage.setItem('password_reset_email', formData.email);
      
      setMessage("Verification code has been sent to your email");
      setMessageType("success");
      setStep(2);
      setCountdown(60);
      
      // 倒计时
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      setIsLoading(false);
    }, 1000);
  };

  // 验证验证码
  const verifyCode = () => {
    const storedCode = sessionStorage.getItem('password_reset_code');
    const storedEmail = sessionStorage.getItem('password_reset_email');
    
    if (storedEmail !== formData.email) {
      setMessage("Email address does not match");
      setMessageType("error");
      return;
    }
    
    if (verificationCode !== storedCode) {
      setMessage("Verification code is incorrect, please try again");
      setMessageType("error");
      return;
    }
    
    setMessage("Verification code verified successfully");
    setMessageType("success");
    setStep(3);
  };

  // 重置密码
  const resetPassword = () => {
    if (formData.newPassword !== formData.confirmPassword) {
      setMessage("Passwords do not match");
      setMessageType("error");
      return;
    }
    
    if (formData.newPassword.length < 6) {
      setMessage("Password must be at least 6 characters long");
      setMessageType("error");
      return;
    }

    // 更新用户密码
    const users = getUsers();
    const updatedUsers = users.map(user => {
      if (user.email === formData.email) {
        return {
          ...user,
          password: formData.newPassword // 在实际项目中应该加密
        };
      }
      return user;
    });

    localStorage.setItem('intelevia_users', JSON.stringify(updatedUsers));
    
    // 清除验证码
    sessionStorage.removeItem('password_reset_code');
    sessionStorage.removeItem('password_reset_email');
    
    setMessage("Password reset successful! Redirecting to login...");
    setMessageType("success");
    
    // 跳转到登录页面
    setTimeout(() => {
      navigate('/auth');
    }, 2000);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setMessage("");
    
    if (step === 1) {
      sendVerificationCode();
    } else if (step === 2) {
      verifyCode();
    } else if (step === 3) {
      resetPassword();
    }
  };

  // 重新发送验证码
  const resendCode = () => {
    if (countdown > 0) return;
    sendVerificationCode();
  };

  return (
    <>
      <SEO noindex={true} nofollow={true} />
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-green-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-emerald-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-green-500 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center animate-slide-in-up">
          <div className="inline-flex items-center px-4 py-2 bg-emerald-100 text-emerald-800 rounded-full text-sm font-medium mb-6">
            <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2 animate-pulse"></span>{tx("Password Reset")}{" "}</div>
          
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-white rounded-2xl shadow-lg flex items-center justify-center animate-float">
              <Shield className="w-8 h-8 text-emerald-600" />
            </div>
          </div>
          
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {tx(step === 1 ? "Reset Your Password" :
             step === 2 ? "Verify Your Email" : "Set New Password")}
          </h2>
          <p className="text-gray-600">
            {tx(step === 1
              ? "Enter your email address to receive a verification code" 
              : step === 2 ? "Enter the verification code sent to your email" :
                "Create a new secure password for your account")
            }
          </p>
        </div>

        {/* Form */}
        <Card className="border-0 shadow-2xl animate-scale-in animate-delay-200">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center text-gray-900">
              {tx(step === 1 ? "Forgot Password" :
               step === 2 ? "Email Verification" : "New Password")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Message notification */}
            {message && (
              <div className={`p-3 rounded-lg text-sm ${
                messageType === "success" 
                  ? "bg-green-50 text-green-700 border border-green-200" 
                  : "bg-red-50 text-red-700 border border-red-200"
              }`}>
                {tx(message)}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Step 1: Enter email */}
              {step === 1 && (
                <div className="space-y-2 animate-fade-in animate-delay-300">
                  <label htmlFor="email" className="text-sm font-medium text-gray-700">{tx("Email Address")}</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="pl-10"
                      placeholder={tx("Enter your email")}
                      onInvalid={(e) => e.target.setCustomValidity(tx('Please enter a valid email address'))}
                      onInput={(e) => e.target.setCustomValidity('')}
                    />
                  </div>
                </div>
              )}

              {/* Step 2: Enter verification code */}
              {step === 2 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">{tx("Verification Code")}</label>
                  <div className="relative">
                    <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      type="text"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      className="pl-10"
                      placeholder={tx("Enter 6-digit code")}
                      maxLength="6"
                    />
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">{tx("Code sent to:")}{" "}{formData.email}
                    </span>
                    <button
                      type="button"
                      onClick={resendCode}
                      disabled={countdown > 0}
                      className={`text-emerald-600 hover:text-emerald-500 ${
                        countdown > 0 ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {countdown > 0 ? tx('Resend in {seconds}s', { seconds: countdown }) : tx('Resend Code')}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Set new password */}
              {step === 3 && (
                <>
                  <div className="space-y-2">
                    <label htmlFor="newPassword" className="text-sm font-medium text-gray-700">{tx("New Password")}</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <Input
                        id="newPassword"
                        name="newPassword"
                        type={showPassword ? "text" : "password"}
                        required
                        value={formData.newPassword}
                        onChange={handleInputChange}
                        className="pl-10 pr-10"
                        placeholder={tx("Enter new password (min 6 characters)")}
                        onInvalid={(e) => e.target.setCustomValidity(tx('Please enter your new password'))}
                        onInput={(e) => e.target.setCustomValidity('')}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">{tx("Confirm New Password")}</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        required
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        className="pl-10 pr-10"
                        placeholder={tx("Confirm new password")}
                        onInvalid={(e) => e.target.setCustomValidity(tx('Please confirm your new password'))}
                        onInput={(e) => e.target.setCustomValidity('')}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </>
              )}

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full gradient-green text-white hover:opacity-90 group animate-pulse-glow disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />{tx("Processing...")}{" "}</>
                ) : (
                  <>
                    {tx(step === 1 ? "Send Verification Code" :
                     step === 2 ? "Verify Code" : "Reset Password")}
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </Button>
            </form>

            {/* Back to Login */}
            <div className="text-center">
              <p className="text-sm text-gray-600">{tx("Remember your password?")}{" "}
                <Link to="/auth" className="text-emerald-600 hover:text-emerald-500 font-medium">{tx("Sign in")}</Link>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Features */}
        <div className="grid grid-cols-3 gap-4 text-center animate-fade-in animate-delay-500">
          <div className="flex flex-col items-center space-y-2">
            <CheckCircle className="w-6 h-6 text-emerald-600 animate-float" />
            <span className="text-sm text-gray-600">{tx("Secure")}</span>
          </div>
          <div className="flex flex-col items-center space-y-2">
            <CheckCircle className="w-6 h-6 text-emerald-600 animate-float animate-delay-200" />
            <span className="text-sm text-gray-600">{tx("Fast")}</span>
          </div>
          <div className="flex flex-col items-center space-y-2">
            <CheckCircle className="w-6 h-6 text-emerald-600 animate-float animate-delay-400" />
            <span className="text-sm text-gray-600">{tx("Reliable")}</span>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
